import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "components/Sidenav/SidenavCollapse";
import SidenavList from "components/Sidenav/SidenavList";
import SidenavItem from "components/Sidenav/SidenavItem";
import SidenavRoot from "components/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/Sidenav/styles/sidenav";
import Switch from "@mui/material/Switch";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setDarkMode,
} from "context/materialUI-context/materialUI.context";
import brandWhite from "assets/images/brands/logo-horizontal-white.png";
import brandIconWhite from "assets/images/brands/icon-white.png";
import brandDark from "assets/images/brands/logo-horizontal-dark.png";
import brandIconDark from "assets/images/brands/icon-dark.png";
import { Route } from "types/route.type";

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName?: string;
  codeClient?: string;
  isSuperAdmin: boolean;
  routes: Route[];
  [key: string]: any;
}

const Sidenav = ({
  color,
  brand,
  brandName,
  routes,
  codeClient,
  isSuperAdmin,
  ...rest
}: Props): JSX.Element => {
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse.map(({ name, route, key, href }: any) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key }: any) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });
  /*
  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }: any) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );
*/
  // Extraire la logique de rendu pour chaque type de route
  const renderCollapseWithHref = (
    { key, href, name, icon, noCollapse }: Route,
    collapseName: string
  ) => (
    <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
      <SidenavCollapse
        name={name}
        icon={icon}
        active={key === collapseName}
        noCollapse={noCollapse}
      />
    </Link>
  );

  const renderCollapseWithRoute = (
    { key, route, name, icon, noCollapse, collapse }: Route,
    collapseName: string,
    renderCollapse: (items: Route[]) => React.ReactNode
  ) => (
    <NavLink to={route} key={key}>
      <SidenavCollapse
        name={name}
        icon={icon}
        noCollapse={noCollapse}
        active={key === collapseName}
      >
        {collapse ? renderCollapse(collapse) : null}
      </SidenavCollapse>
    </NavLink>
  );

  const renderCollapseDefault = (
    { key, name, icon, collapse }: Route,
    collapseName: string,
    openCollapse: string | boolean,
    setOpenCollapse: (key: string | boolean) => void,
    renderCollapse: (items: Route[]) => React.ReactNode
  ) => (
    <SidenavCollapse
      key={key}
      name={name}
      icon={icon}
      active={key === collapseName}
      open={openCollapse === key}
      onClick={() => setOpenCollapse(openCollapse === key ? false : key)}
    >
      {collapse ? renderCollapse(collapse) : null}
    </SidenavCollapse>
  );

  const renderTitle = ({ key, title }: Route, textColor: string) => (
    <MDTypography
      key={key}
      color={textColor}
      display="block"
      variant="caption"
      fontWeight="bold"
      textTransform="uppercase"
      pl={3}
      mt={2}
      mb={1}
      ml={1}
    >
      {title}
    </MDTypography>
  );

  const renderDivider = (
    { key }: Route,
    darkMode: boolean,
    whiteSidenav: boolean,
    transparentSidenav: boolean
  ) => (
    <Divider
      key={key}
      light={
        (!darkMode && !whiteSidenav && !transparentSidenav) ||
        (darkMode && !transparentSidenav && whiteSidenav)
      }
    />
  );

  // Fonction principale avec complexité réduite
  const renderRoutes = (items: Route[]): JSX.Element[] => {
    return items.map((route: Route) => {
      if (!route.type) return null;

      switch (route.type) {
        case "collapse":
          if (route.href) {
            return renderCollapseWithHref(route, collapseName);
          }
          if (route.noCollapse && route.route) {
            return renderCollapseWithRoute(route, collapseName, renderCollapse);
          }
          return renderCollapseDefault(
            route,
            collapseName,
            openCollapse,
            setOpenCollapse,
            renderCollapse
          );

        case "title":
          return renderTitle(route, textColor);

        case "divider":
          return renderDivider(route, darkMode, whiteSidenav, transparentSidenav);

        default:
          return null;
      }
    });
  };

  const brandHopelapp =
    (transparentSidenav && !darkMode) || whiteSidenav ? brandIconDark : brandIconWhite;
  const brandNameHopelapp =
    (transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite;
  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <MDBox pt={2} pb={1} px={2} textAlign="center">
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && <MDBox component="img" src={brand} alt="Brand" width="3.6rem" />}
            <MDBox
              width={!brandNameHopelapp && "100%"}
              pt={1}
              sx={(theme: any) => ({ ...sidenavLogoLabel(theme, { miniSidenav }), minWidth: 0, flex: 1 })}
            >
              <Tooltip title={brandName || ""} placement="right" enterDelay={500} disableInteractive>
                <MDTypography
                  component="h1"
                  variant="button"
                  fontWeight="medium"
                  fontSize={18}
                  color={textColor}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    display: "block",
                  }}
                >
                  {brandName}
                </MDTypography>
              </Tooltip>
            </MDBox>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" fontWeight="medium" fontSize={12} color={textColor}>
              {`${!miniSidenav ? "Code : " : ""}${codeClient}`}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        <MDBox
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <List sx={{ p: 0 }}>{renderRoutes(routes)}</List>
        </MDBox>
      </MDBox>
      {/* Footer Section */}
      <MDBox
        sx={{
          px: 1.5,
          py: 1,
          mb: 1.5,
        }}
      >
        {/* Theme Switch */}
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav ? "center" : "space-between"}
          px={1}
          py={0.6}
          mb={1}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
            },
          }}
        >
          {miniSidenav ? (
            <Tooltip title={darkMode ? "Mode clair" : "Mode sombre"} placement="right">
              <Icon
                sx={{ color: "white", fontSize: "1rem", cursor: "pointer" }}
                onClick={() => setDarkMode(dispatch, !darkMode)}
              >
                {darkMode ? "dark_mode" : "light_mode"}
              </Icon>
            </Tooltip>
          ) : (
            <>
              <MDBox display="flex" alignItems="center">
                <Icon sx={{ color: "white", mr: 0.8, fontSize: "1rem" }}>
                  {darkMode ? "dark_mode" : "light_mode"}
                </Icon>
                <MDTypography variant="caption" fontWeight="medium" color="white" sx={{ fontSize: "0.75rem" }}>
                  {darkMode ? "Mode sombre" : "Mode clair"}
                </MDTypography>
              </MDBox>
              <Switch
                checked={darkMode}
                onChange={() => setDarkMode(dispatch, !darkMode)}
                size="small"
                sx={{
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "& .Mui-checked .MuiSwitch-thumb": {
                    backgroundColor: "#e3b025",
                  },
                  "& .Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "rgba(227, 176, 37, 0.5)",
                  },
                }}
              />
            </>
          )}
        </MDBox>

        {/* Hopelapp Logo */}
        <MDBox
          component={NavLink}
          target="_blank"
          to="https://hopelapp.com"
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={0.5}
          sx={{
            transition: "all 0.2s ease",
            opacity: 0.6,
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <MDBox component="img" src={brandHopelapp} alt="Hopelapp" width="1.4rem" />
          {!miniSidenav && (
            <MDTypography
              variant="caption"
              color="white"
              sx={{ ml: 0.8, fontSize: "0.65rem", opacity: 0.8 }}
            >
              Propulsé par Hopelapp
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </SidenavRoot>
  );
};

// Declaring default props for Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
